import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Checkbox,
  FormControlLabel
} from '@mui/material';
// Using TextField for dates instead of DatePicker to avoid compatibility issues
import { motion } from 'framer-motion';
import axios from 'axios';
import Confetti from 'react-confetti';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

const ElectionDates = ({ setLoading, setError, setSuccess }) => {
  const [elections, setElections] = useState([]);
  const [selectedElectionId, setSelectedElectionId] = useState(null);
  const [electionName, setElectionName] = useState('');
  const [seatsToContest, setSeatsToContest] = useState([1, 2, 3, 4, 5]);
  const [currentWinners, setCurrentWinners] = useState({});
  const [nominationStartDate, setNominationStartDate] = useState('');
  const [nominationEndDate, setNominationEndDate] = useState('');
  const [votingStartDate, setVotingStartDate] = useState('');
  const [votingEndDate, setVotingEndDate] = useState('');
  const [showConfetti, setShowConfetti] = useState(false);
  
  // Fetch existing elections and current winners
  useEffect(() => {
    const fetchElections = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/elections/');
        setElections(response.data);
        setLoading(false);
      } catch (err) {
        // Convert any error object to a string to avoid React rendering issues
        const errorMessage = typeof err.response?.data?.detail === 'object'
          ? JSON.stringify(err.response?.data?.detail)
          : (err.response?.data?.detail || 'Failed to fetch elections');
        setError(errorMessage);
        setLoading(false);
      }
    };

    const fetchCurrentWinners = async () => {
      try {
        const response = await axios.get('/api/votes/winners');
        setCurrentWinners(response.data);
      } catch (err) {
        console.error('Failed to fetch current winners:', err);
      }
    };

    fetchElections();
    fetchCurrentWinners();
  }, [setLoading, setError]);
  
  // Edit an existing election
  const handleEditElection = (election) => {
    setSelectedElectionId(election.id);
    setElectionName(election.name);
    setSeatsToContest(election.seats_to_contest || [1, 2, 3, 4, 5]);
    setNominationStartDate(election.nomination_start_date.split('T')[0]);
    setNominationEndDate(election.nomination_end_date.split('T')[0]);
    setVotingStartDate(election.voting_start_date.split('T')[0]);
    setVotingEndDate(election.voting_end_date.split('T')[0]);
  };
  
  // Reset the form
  const handleReset = () => {
    setSelectedElectionId(null);
    setElectionName('');
    setSeatsToContest([1, 2, 3, 4, 5]);
    setNominationStartDate('');
    setNominationEndDate('');
    setVotingStartDate('');
    setVotingEndDate('');
  };
  
  // Delete an election
  const handleDeleteElection = async (id) => {
    if (window.confirm('Are you sure you want to delete this election?')) {
      try {
        setLoading(true);
        await axios.delete(`/api/elections/${id}`);
        // Refresh the elections list
        const response = await axios.get('/api/elections/');
        setElections(response.data);
        setSuccess('Election deleted successfully!');
        handleReset(); // Reset the form if the deleted election was being edited
        setLoading(false);
      } catch (err) {
        // Convert any error object to a string to avoid React rendering issues
        const errorMessage = typeof err.response?.data?.detail === 'object' 
          ? JSON.stringify(err.response?.data?.detail) 
          : (err.response?.data?.detail || 'Failed to delete election');
        setError(errorMessage);
        setLoading(false);
      }
    }
  };

  // Save election
  const handleSave = async () => {
    if (!electionName || !nominationStartDate || !nominationEndDate || !votingStartDate || !votingEndDate) {
      setError('All fields are required');
      return;
    }
    
    // Helpers to build start/end-of-day ISO (force UTC to prevent timezone shifting)
    const toISOAt = (dateStr, h, m, s, ms) => {
      if (!dateStr) return null;
      const [y, mo, d] = dateStr.split('-').map(Number);
      
      // Create date directly in UTC to prevent timezone shifting
      const dt = new Date(Date.UTC(y, (mo || 1) - 1, d || 1, h, m, s, ms));
      
      console.log(`INPUT: ${dateStr} -> PARSED: ${y}-${mo}-${d} -> UTC DATE: ${dt} -> ISO: ${dt.toISOString()}`);
      
      return dt.toISOString();
    };
    const startOfDayISO = (dateStr) => toISOAt(dateStr, 0, 0, 0, 0);
    const endOfDayISO = (dateStr) => toISOAt(dateStr, 23, 59, 59, 999);

    // Build concrete instants for validation
    const nomStart = new Date(startOfDayISO(nominationStartDate));
    const nomEnd = new Date(endOfDayISO(nominationEndDate));
    const voteStart = new Date(startOfDayISO(votingStartDate));
    const voteEnd = new Date(endOfDayISO(votingEndDate));

    // Validate sequences (inclusive end-of-day semantics)
    if (!(nomEnd >= nomStart)) {
      setError('Nomination end date must be on or after start date');
      return;
    }
    if (!(voteStart > nomEnd)) {
      setError('Voting must start after the nomination end date');
      return;
    }
    if (!(voteEnd > voteStart)) {
      setError('Voting end date must be after voting start date');
      return;
    }
    
    const electionData = {
      name: electionName,
      seats_to_contest: seatsToContest,
      nomination_start_date: startOfDayISO(nominationStartDate),
      nomination_end_date: endOfDayISO(nominationEndDate),
      voting_start_date: startOfDayISO(votingStartDate),
      voting_end_date: endOfDayISO(votingEndDate)
    };
    
    try {
      setLoading(true);
      
      if (selectedElectionId) {
        // Update existing election
        await axios.put(`/api/elections/${selectedElectionId}`, electionData);
        setSuccess('Election updated successfully!');
      } else {
        // Create new election
        await axios.post('/api/elections/', electionData);
        setSuccess('New election created successfully!');
      }
      
      // Refresh elections
      const response = await axios.get('/api/elections/');
      setElections(response.data);
      
      // Reset form
      handleReset();
      
      // Show confetti animation
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 5000);
      
      setLoading(false);
    } catch (err) {
      // Convert any error object to a string to avoid React rendering issues
      const errorMessage = typeof err.response?.data?.detail === 'object' 
        ? JSON.stringify(err.response?.data?.detail) 
        : (err.response?.data?.detail || 'Failed to save election');
      setError(errorMessage);
      setLoading(false);
    }
  };
  
  return (
    <Box>
      {showConfetti && (
        <Confetti
          colors={['#99D6D6', '#009999', '#702963']}
          recycle={false}
          numberOfPieces={500}
        />
      )}
      
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 500 }}>
          Set Election Dates
        </Typography>
        
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: '14px',
            background: 'rgba(112, 128, 144, 0.4)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            {selectedElectionId ? 'Edit Election' : 'Create New Election'}
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Election Name"
                fullWidth
                value={electionName}
                onChange={(e) => setElectionName(e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                Seats to Contest (select which seats will be up for election)
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2 }}>
                {[1, 2, 3, 4, 5].map((seatNum) => {
                  const winner = currentWinners[`seat_${seatNum}`];
                  const isSelected = seatsToContest.includes(seatNum);

                  return (
                    <Paper
                      key={seatNum}
                      onClick={() => {
                        if (isSelected) {
                          setSeatsToContest(prev => prev.filter(s => s !== seatNum));
                        } else {
                          setSeatsToContest(prev => [...prev, seatNum]);
                        }
                      }}
                      sx={{
                        p: 2,
                        border: isSelected ? '2px solid #009999' : '1px solid rgba(0,0,0,0.12)',
                        backgroundColor: isSelected ? 'rgba(0, 153, 153, 0.05)' : 'white',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        Seat {seatNum}
                      </Typography>
                      {winner && !winner.is_vacant ? (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {winner.candidate_name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                            {winner.business_unit || 'No business unit'}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                          Vacant
                        </Typography>
                      )}
                    </Paper>
                  );
                })}
              </Box>
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                {seatsToContest.length} seat{seatsToContest.length !== 1 ? 's' : ''} selected
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Nomination Start Date"
                type="date"
                fullWidth
                value={nominationStartDate}
                onChange={(e) => setNominationStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Nomination End Date"
                type="date"
                fullWidth
                value={nominationEndDate}
                onChange={(e) => setNominationEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Voting Start Date"
                type="date"
                fullWidth
                value={votingStartDate}
                onChange={(e) => setVotingStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Voting End Date"
                type="date"
                fullWidth
                value={votingEndDate}
                onChange={(e) => setVotingEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                {selectedElectionId && (
                  <Button
                    variant="outlined"
                    onClick={handleReset}
                  >
                    Cancel
                  </Button>
                )}
                
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                  sx={{
                    '&:hover': {
                      transform: 'scale(1.02)',
                      backgroundColor: '#008080'
                    }
                  }}
                >
                  {selectedElectionId ? 'Update' : 'Create'} Election
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
        
        {/* Elections Table */}
        {elections.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: '14px',
              background: 'rgba(112, 128, 144, 0.4)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              Current Elections
            </Typography>
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Seats to Contest</TableCell>
                    <TableCell>Nomination Period</TableCell>
                    <TableCell>Voting Period</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {elections.map((election) => {
                    // Format dates for display
                    const nomStart = new Date(election.nomination_start_date).toLocaleDateString();
                    const nomEnd = new Date(election.nomination_end_date).toLocaleDateString();
                    const voteStart = new Date(election.voting_start_date).toLocaleDateString();
                    const voteEnd = new Date(election.voting_end_date).toLocaleDateString();
                    
                    return (
                      <TableRow key={election.id}>
                        <TableCell>{election.name}</TableCell>
                        <TableCell>
                          {election.seats_to_contest ? election.seats_to_contest.join(', ') : (election.seats_to_fill || 5)}
                        </TableCell>
                        <TableCell>{nomStart} - {nomEnd}</TableCell>
                        <TableCell>{voteStart} - {voteEnd}</TableCell>
                        <TableCell>
                          <IconButton
                            color="primary"
                            onClick={() => handleEditElection(election)}
                            size="small"
                          >
                            <EditIcon />
                          </IconButton>

                          <IconButton
                            color="error"
                            onClick={() => handleDeleteElection(election.id)}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </motion.div>
    </Box>
  );
};

export default ElectionDates;
